"use client";

import * as React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Flame, MapPin, PhoneCall, Search, ShieldCheck, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const coreServices = [{
  title: "Emergency Plumbing",
  path: "/emergency-plumbing/",
  icon: Zap
}, {
  title: "Drain Cleaning",
  path: "/drain-cleaning/",
  icon: Search
}, {
  title: "Water Heater Repair",
  path: "/water-heater-repair/",
  icon: Flame
}, {
  title: "Leak Detection",
  path: "/leak-detection-repair/",
  icon: ShieldCheck
}, {
  title: "Toilet Repair",
  path: "/toilet-repair/",
  icon: CheckCircle2
}, {
  title: "Sewer Line Service",
  path: "/sewer-line-repair/",
  icon: Wrench
}, {
  title: "Pipe Repair",
  path: "/pipe-repair/",
  icon: Clock
}, {
  title: "General Plumbing",
  path: "/services/",
  icon: CheckCircle2
}];
export default function JonesboroCallout() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="jonesboroflagshipcallout" data-nav="light" className="bg-background text-foreground py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants}>
          <Card className="relative overflow-hidden rounded-2xl border border-primary/20 bg-primary text-primary-foreground shadow-2xl py-8 md:py-12">
            {/* Background subtle decorative radial gradients */}
            <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

            <CardContent className="relative z-10 px-6 sm:px-10 md:px-14 flex flex-col gap-8">
              {/* Top Row: Badge & Local Guarantee */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-accent font-semibold px-3 py-1 text-xs sm:text-sm tracking-wide uppercase gap-1.5 shadow-sm">
                  <MapPin className="size-3.5" />
                  <span>Flagship Service Area</span>
                </Badge>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90 font-medium">
                  <span className="inline-block size-2 rounded-full bg-accent animate-pulse" />
                  <span>Rapid Response in Clayton County</span>
                </div>
              </div>

              {/* Main Heading & Intro */}
              <div className="max-w-3xl space-y-3">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-primary-foreground">
                  Plumbing Services in Jonesboro, GA
                </h2>
                <p className="text-primary-foreground/85 text-sm sm:text-base leading-relaxed">
                  Headquartered in Jonesboro, we prioritize local emergencies with fast response times, licensed local technicians, and fully equipped service vehicles ready 24/7.
                </p>
              </div>

              {/* 8-Item Core Services Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {coreServices.map((service, i) => {
                const Icon = service.icon;
                return <div data-index={i} key={service.title} className="h-full">
                      <Link to={service.path} className="group flex items-center gap-3 p-3.5 rounded-lg bg-primary-foreground/10 hover:bg-accent hover:text-accent-foreground transition-colors border border-primary-foreground/15 hover:border-accent shadow-xs h-full">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Icon className="size-4" />
                        </div>
                        <span className="text-sm font-medium tracking-tight text-primary-foreground group-hover:text-accent-foreground transition-colors">
                          {service.title}
                        </span>
                      </Link>
                    </div>;
              })}
              </div>

              {/* Bottom Actions Row */}
              <div className="pt-4 border-t border-primary-foreground/15 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-5">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md gap-2">
                    <Link to="/jonesboro-service-area/">
                      <span>Explore Jonesboro Plumbing Services</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground font-semibold">
                    <Link to="/contact/">
                      <span>Schedule Online</span>
                    </Link>
                  </Button>
                </div>

                {/* Direct Dial Emergency Prompt */}
                <div className="flex items-center gap-3 bg-primary-foreground/10 px-4 py-2.5 rounded-lg border border-primary-foreground/15">
                  <div className="size-9 rounded-full bg-accent flex items-center justify-center text-accent-foreground shrink-0">
                    <PhoneCall className="size-4" />
                  </div>
                  <div>
                    <span className="block text-2xs uppercase tracking-wider text-primary-foreground/75 font-semibold">
                      Jonesboro 24/7 Dispatch
                    </span>
                    <a href="tel:4708479076" className="text-sm font-bold text-accent hover:underline inline-flex items-center gap-1">
                      (470) 847-9076
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}